import { useId, type ChangeEvent, type KeyboardEvent } from "react";
import { cn } from "../../../lib/cn";
import styles from "./ChipGroup.module.css";

export interface ChipOption<T extends string> {
  value: T;
  label: string;
}

export interface ChipGroupProps<T extends string> {
  legend: string;
  name: string;
  options: readonly ChipOption<T>[];
  value: T;
  onChange: (value: T) => void;
  /** Secondary line under the legend, for the meaning behind the choice. */
  hint?: string;
}

/**
 * Single-select segmented control.
 *
 * Backed by real radio inputs rather than buttons, which is what makes it behave
 * for keyboard users: a radio group is a single tab stop and the arrow keys move
 * the selection inside it. The button version this replaces put every option in
 * the tab order, so Priority and Reported by cost six stops between the
 * description and the timing fields.
 *
 * The inputs are visually hidden but never `display: none` — they stay focusable
 * and the focus ring is drawn on the adjacent label.
 */
export function ChipGroup<T extends string>({
  legend,
  name,
  options,
  value,
  onChange,
  hint,
}: ChipGroupProps<T>) {
  const hintId = useId();

  const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
    onChange(event.target.value as T);
  };

  /*
   * A radio hands Enter to the form's implicit submission, which the buttons this
   * replaced used to absorb. Selection here is the arrow keys' job, so Enter has
   * nothing left to do and must not submit a form the user is still filling in.
   */
  const handleKeyDown = (event: KeyboardEvent<HTMLFieldSetElement>): void => {
    if (event.key === "Enter") event.preventDefault();
  };

  return (
    <fieldset className={styles.root} onKeyDown={handleKeyDown}>
      <legend className={styles.legend}>{legend}</legend>
      {hint ? (
        <span className={styles.hint} id={hintId}>
          {hint}
        </span>
      ) : null}
      <div className={styles.group}>
        {options.map((option) => (
          <label
            key={option.value}
            className={cn(styles.segment, { [styles.segmentActive]: option.value === value })}
          >
            <input
              type="radio"
              className={styles.radio}
              name={name}
              value={option.value}
              checked={option.value === value}
              aria-describedby={hint ? hintId : undefined}
              onChange={handleChange}
            />
            <span className={styles.segmentLabel}>{option.label}</span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}
